import { gql } from "@apollo/client";

export const PRODUCT_FRAGMENT = gql`
  fragment ProductFragment on ProductObject {
    id
    name
    skuCode
    hasStock
    sellingPrice
    slug
    isEnabled
    isReward
    coverPhotoSmallPng
    coverPhotoLargePng
    backgroundColor
    descriptionMarkdown
    productionCountry {
      id
      name
    }
    productType {
      id
      name
      isEnabled
      canCountTowardsOrderMinimum
    }
    wine {
      id
      product {
        id
        name
        productType {
          id
        }
      }
      country {
        id
        name
      }
      wineRegion {
        id
        name
        state
      }
      wineType {
        id
        name
        wineClass {
          id
          name
        }
      }
      wineMaker {
        id
        name
      }
    }
  }
`;

export const CELLAR_PRODUCT_FRAGMENT = gql`
  fragment CellarProductFragment on ProductObject {
    id
    name
    skuCode
    sellingPrice
    isEnabled
    isReward
    coverPhotoSmallPng
    coverPhotoSmall
    coverPhotoLargePng
    descriptionMarkdown
    productionCountry {
      id
      name
    }
    hasStock
    slug
    backgroundColor
    lastOrderByMember {
      createdDate
      order {
        chargeDate
      }
      recommendationExplanation {
        id
        explanation
        explanationType
        rating
        feedbackText
        feedbackTags
        feedbackSubmittedDate
      }
    }
    productType {
      id
      name
      isEnabled
    }
    productRating {
      score
      note
      details {
        attribute {
          id
          key
        }
        scaleValue
        booleanValue
      }
    }
    ratingAttributes {
      id
      key
      label
      kind
    }
    ratingPoints
    productPhotos {
      # id keeps these normalized: the wine tray's GET_WINE_NAME_AND_LARGE_PHOTO
      # writes productPhotos { id photoLarge } for the same product, and without
      # a shared id that write replaces this selection's photoSmall — leaving the
      # cellar listing partial, which partialRefetch answers by re-running the
      # whole listing from offset 0.
      id
      photoSmall
    }
    wine {
      hasStock
      hasOtherVintageStock
      id
      country {
        id
        name
      }
      wineRegion {
        id
        name
        state
      }
      wineType {
        id
        name
        wineClass {
          id
          name
        }
      }
      wineMaker {
        id
        name
      }
    }
  }
`;

export const COLLECTION_FRAGMENT = gql`
  fragment CollectionFragment on CollectionObject {
    id
    name
    description
    slug
    products(search: $search, limit: $productLimit, offset: $productOffset) {
      ...ProductFragment
    }
  }
  ${PRODUCT_FRAGMENT}
`;

export const COLLECTION_FRAGMENT_WITH_PRODUCT_COUNT = gql`
  fragment CollectionFragmentWithProductCount on CollectionObject {
    id
    name
    description
    slug
    productCount(search: $search)
    products(search: $search, limit: $productLimit, offset: $productOffset, orderBy: $orderBy) {
      ...ProductFragment
    }
  }
  ${PRODUCT_FRAGMENT}
`;

/* One-shot query for the native BrowseSheet. Counts mirror the three
 * root-level scalars the RN app reads (see mobile repo's
 * GET_ALL_PRODUCTS_IN_COLLECTIONS_COUNT etc.):
 *  - `menuCollections` → dynamic category tiles (Beer, Spirits, Sake, …).
 *  - `allWinesByWineListCount(wineListSlug: "all-wines")` → "Explore Wines".
 *  - `allProductsInCollectionsCount` → "Explore More". Single source of
 *    truth from the backend; client-side summing of collection productCount
 *    double-counts because products live in multiple collections.
 *  - `memberOrderItemsWithRatingCount` → "Past Drinks", guarded by
 *    `$skipMemberCellar` so logged-out users don't request it. */
export const GET_BROWSE_SHEET_DATA = gql`
  query GetBrowseSheetData($skipMemberCellar: Boolean = true) {
    menuCollections: collections(isShownInMenu: true) {
      id
      name
      slug
    }
    allWinesByWineListCount(wineListSlug: "all-wines")
    allProductsInCollectionsCount
    memberOrderItemsWithRatingCount @skip(if: $skipMemberCellar)
  }
`;

export const GET_PRODUCT_COLLECTIONS_WITH_COUNT = gql`
  query GetProductCollectionsWithCount(
    $limit: Int
    $offset: Int
    $search: String
    $productLimit: Int
    $productOffset: Int
  ) {
    collectionsCount(search: $search, excludeEmptyCollections: true)
    collections(limit: $limit, offset: $offset, search: $search, excludeEmptyCollections: true) {
      ...CollectionFragment
    }
  }
  ${COLLECTION_FRAGMENT}
`;

export const FETCH_MORE_PRODUCT_COLLECTIONS = gql`
  query FetchMoreProductCollections(
    $limit: Int
    $offset: Int
    $search: String
    $productLimit: Int
    $productOffset: Int
  ) {
    collections(limit: $limit, offset: $offset, search: $search, excludeEmptyCollections: true) {
      ...CollectionFragment
    }
  }
  ${COLLECTION_FRAGMENT}
`;

export const GET_COLLECTION_BY_SLUG_WITH_PRODUCT_COUNT = gql`
  query GetCollectionBySlugWithProductCount(
    $slug: String!
    $search: String
    $productLimit: Int
    $productOffset: Int
    $orderBy: CollectionProductOrderField
  ) {
    collectionBySlug(slug: $slug) {
      ...CollectionFragmentWithProductCount
    }
  }
  ${COLLECTION_FRAGMENT_WITH_PRODUCT_COUNT}
`;

export const FETCH_MORE_PRODUCTS_BY_COLLECTION_SLUG = gql`
  query FetchMoreProductsByCollectionSlug(
    $slug: String!
    $search: String
    $productLimit: Int
    $productOffset: Int
    $orderBy: CollectionProductOrderField
  ) {
    collectionBySlug(slug: $slug) {
      ...CollectionFragmentWithProductCount
    }
  }
  ${COLLECTION_FRAGMENT_WITH_PRODUCT_COUNT}
`;

export const ADD_PRODUCT_TO_CART = gql`
  mutation AddProductToCart($input: AddShoppingCartItemInput!) {
    addShoppingCartItem(input: $input) {
      shoppingCart {
        id
        totalNonSubscriptionProductsCost
        amountPresents
        bottlesTowardsOrderMinimum
        shoppingcartitemSet {
          quantity
          product {
            id
            hasStock
            name
            skuCode
            sellingPrice
            backgroundColor
            coverPhotoSmallPng
            productType {
              id
              name
            }
            productionCountry {
              id
              name
            }
          }
        }
      }
      errors {
        messages
      }
    }
  }
`;

export const ADD_PRODUCT_TO_BOX = gql`
  mutation AddProductToBox($productId: Int!, $quantity: Int!, $redeemWithPoints: Boolean!) {
    addProductToNextBox(productId: $productId, quantity: $quantity, redeemWithPoints: $redeemWithPoints) {
      errors {
        messages
      }
      subscription {
        id
        amountPresents
        bottlesTowardsOrderMinimum
        subscriptionnextboxproductSet {
          id
          quantity
          isRedeemedWithPoints
          product {
            id
            name
            slug
            sellingPrice
            pointCost
            coverPhotoSmall
            hasStock
            backgroundColor
            productType {
              id
              name
            }
            productionCountry {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export const GET_COLLECTIONS_PRODUCT_ITEM_BY_SLUG = gql`
  query CollectionsProductItemById($slug: String!) {
    productBySlug(slug: $slug) {
      ...ProductFragment
    }
  }
  ${PRODUCT_FRAGMENT}
`;

export const GET_PRODUCT_ITEM_BY_SLUG = gql`
  query ProductItemBySlug($slug: String!) {
    productBySlug(slug: $slug) {
      ...ProductFragment
      coverPhotoSmall
      productRating {
        score
        note
      }
      lastOrderByMember {
        createdDate
      }
      productPhotos {
        id
        photoSmall
      }
    }
  }
  ${PRODUCT_FRAGMENT}
`;

/* The detail values for one product rating plus the attribute set its product
 * type offers — enough to render both the entry rows and the tray read-back.
 * The rating's fields must stay a superset of what CellarProductFragment
 * selects on productRating: Apollo replaces the whole unnormalised
 * productRating object on merge, so a narrower selection would drop fields
 * (e.g. note) from the cached cellar cards. */
export const GET_PRODUCT_RATING_DETAILS = gql`
  query GetProductRatingDetails($slug: String!) {
    productBySlug(slug: $slug) {
      id
      ratingAttributes {
        id
        key
        label
        kind
      }
      productRating {
        score
        note
        details {
          attribute {
            id
            key
          }
          scaleValue
          booleanValue
        }
      }
    }
  }
`;

export const UPDATE_PRODUCT_RATING_DETAILS = gql`
  mutation UpdateProductRatingDetails($input: ProductRatingDetailsInput!) {
    updateProductRatingDetails(input: $input) {
      product {
        id
        productRating {
          score
          note
          details {
            attribute {
              id
              key
            }
            scaleValue
            booleanValue
          }
        }
      }
      errors {
        field
        messages
      }
    }
  }
`;

export const UPDATE_PRODUCT_RATING = gql`
  mutation UpdateProductRating($input: ProductRatingInput!) {
    updateProductRating(input: $input) {
      product {
        ...CellarProductFragment
      }
      pointsEarned
      randomVariableRewardPoints
      errors {
        field
        messages
      }
    }
  }
  ${CELLAR_PRODUCT_FRAGMENT}
`;
